import React, { useState, useEffect, useRef, useCallback } from "react";
import { qualityAPI, usersAPI, adminAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { Plus, X, Star, Camera, Upload, AlertTriangle, CheckCircle, Loader, Thermometer } from "lucide-react";

const RATINGS = [1,2,3,4,5];
const CONDITIONS = [
  { value: "good", label: "Good", color: "text-[#637C64] bg-[#EDF2EE]" },
  { value: "acceptable", label: "Acceptable", color: "text-[#D98032] bg-[#FDF4EB]" },
  { value: "poor", label: "Poor", color: "text-[#C2624E] bg-[#FBEAE7]" },
  { value: "rejected", label: "Rejected", color: "text-white bg-[#C2624E]" },
];

function RatingInput({ value, onChange, label }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">{label}</label>
      <div className="flex gap-1">
        {RATINGS.map(n => (
          <button key={n} type="button" onClick={() => onChange(n)}
            className={`w-9 h-9 rounded-lg text-sm font-bold border-2 transition-all ${n <= value ? "border-[#E8956D] bg-[#E8956D]/10 text-[#E8956D]" : "border-[#E5E1DB] text-[#C4BAAF]"}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "food", label: "Food Quality Inspection" },
  { id: "material", label: "Material Quality at Gate" },
];

export default function QualityInspection() {
  const { selectedKitchen } = useKitchen();
  const [activeTab, setActiveTab] = useState("food");
  const [foodRecords, setFoodRecords] = useState([]);
  const [materialRecords, setMaterialRecords] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [itemsChecked, setItemsChecked] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const photoRef = useRef();
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    adminAPI.getMasterItems().then(r => setMasterItems(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [activeTab, selectedKitchen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "food") {
        const r = await qualityAPI.getFoodQuality({ kitchen_id: selectedKitchen || null });
        setFoodRecords(r.data || []);
      } else {
        const r = await qualityAPI.getMaterialQuality({ kitchen_id: selectedKitchen || null });
        setMaterialRecords(r.data || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSaveFood = async () => {
    setError("");
    try {
      await qualityAPI.createFoodQuality({ ...form, kitchen_id: selectedKitchen || null });
      setModal(null); setForm({});
      fetchData();
      setSuccess("Quality inspection saved!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const handleSaveMaterial = async () => {
    setError("");
    try {
      await qualityAPI.createMaterialQuality({
        ...form,
        kitchen_id: selectedKitchen || null,
        items_checked: itemsChecked,
        photos
      });
      setModal(null); setForm({}); setItemsChecked([]); setPhotos([]);
      fetchData();
      setSuccess("Material quality check saved!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const addItemCheck = () => setItemsChecked(prev => [...prev, {
    item_name: "", bill_qty: 0, received_qty: 0, freshness_rating: 3, condition: "good", rejected_qty: 0
  }]);

  const updateItemCheck = (i, field, val) => setItemsChecked(prev => {
    const updated = [...prev];
    updated[i] = { ...updated[i], [field]: val };
    return updated;
  });

  const addPhoto = (file) => {
    const reader = new FileReader();
    reader.onload = e => setPhotos(prev => [...prev, e.target.result]);
    reader.readAsDataURL(file);
  };

  const avgRating = (r) => ((r.taste_rating + r.appearance_rating + r.hygiene_rating) / 3).toFixed(1);
  const condConfig = (c) => CONDITIONS.find(x => x.value === c) || CONDITIONS[0];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="quality-inspection">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Quality Inspection</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Food quality checks + Material quality at gate</p>
        </div>
        <button
          onClick={() => { setForm({ date: today, taste_rating: 3, appearance_rating: 3, hygiene_rating: 3, meal_type: "lunch", complaints_count: 0 }); setModal(activeTab); setError(""); setItemsChecked([{ item_name: "", bill_qty: 0, received_qty: 0, freshness_rating: 3, condition: "good", rejected_qty: 0 }]); setPhotos([]); }}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 font-bold text-sm flex items-center gap-1.5">
          <Plus size={14} /> Log Inspection
        </button>
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}

      <div className="flex border-b border-[#E5E1DB]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <>
          {activeTab === "food" && (
            <div className="space-y-3">
              {foodRecords.map(r => (
                <div key={r.id} className={`bg-white border rounded-xl p-4 ${r.complaints_count > 0 ? "border-[#C2624E]" : "border-[#E5E1DB]"}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-[#3A322C]">{r.date} — {r.meal_type?.charAt(0).toUpperCase() + r.meal_type?.slice(1)}</p>
                        {r.complaints_count > 0 && <span className="text-xs bg-[#FBEAE7] text-[#C2624E] font-bold px-2 py-0.5 rounded-full">{r.complaints_count} complaint(s)</span>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#8A7E73]">
                        {r.temp_at_packing && <span className="flex items-center gap-1"><Thermometer size={11} />Pack: {r.temp_at_packing}°C</span>}
                        {r.temp_at_delivery && <span className="flex items-center gap-1"><Thermometer size={11} />Delivery: {r.temp_at_delivery}°C</span>}
                        {r.inspector_name && <span>By: {r.inspector_name}</span>}
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-extrabold font-[Manrope] text-[#E8956D]">{r.avg_rating || avgRating(r)}</p>
                      <p className="text-xs text-[#8A7E73]">avg rating</p>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-3">
                    {[["Taste", r.taste_rating], ["Appearance", r.appearance_rating], ["Hygiene", r.hygiene_rating]].map(([label, val]) => (
                      <div key={label} className="text-center">
                        <p className={`text-base font-bold ${val >= 4 ? "text-[#637C64]" : val <= 2 ? "text-[#C2624E]" : "text-[#D98032]"}`}>{val}/5</p>
                        <p className="text-xs text-[#8A7E73]">{label}</p>
                      </div>
                    ))}
                  </div>
                  {r.complaint_details && <p className="text-xs text-[#C2624E] mt-2 bg-[#FBEAE7] px-3 py-1.5 rounded-lg">{r.complaint_details}</p>}
                  {r.notes && <p className="text-xs text-[#8A7E73] mt-1.5">{r.notes}</p>}
                </div>
              ))}
              {foodRecords.length === 0 && <div className="text-center py-16 text-[#8A7E73]"><CheckCircle size={48} className="mx-auto mb-3 opacity-20" /><p>No food quality records yet</p></div>}
            </div>
          )}

          {activeTab === "material" && (
            <div className="space-y-3">
              {materialRecords.map(r => {
                const cond = condConfig(r.overall_condition);
                return (
                  <div key={r.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-bold text-[#3A322C]">{r.date} — {r.supplier_name || "Supplier"}</p>
                        <p className="text-xs text-[#8A7E73]">{r.vehicle_number || ""} {r.inspector_name ? `· Inspected by: ${r.inspector_name}` : ""}</p>
                      </div>
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${cond.color}`}>{cond.label}</span>
                    </div>
                    {r.items_checked?.length > 0 && (
                      <div className="text-xs text-[#8A7E73]">{r.items_checked.length} items checked</div>
                    )}
                    {r.notes && <p className="text-xs text-[#8A7E73] mt-1.5">{r.notes}</p>}
                  </div>
                );
              })}
              {materialRecords.length === 0 && <div className="text-center py-16 text-[#8A7E73]"><AlertTriangle size={48} className="mx-auto mb-3 opacity-20" /><p>No material quality records yet</p></div>}
            </div>
          )}
        </>
      )}

      {/* Food Quality Modal */}
      {modal === "food" && (
        <Modal title="Log Food Quality Inspection" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date *</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.date || today} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Meal Type</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.meal_type || "lunch"} onChange={e => setForm({ ...form, meal_type: e.target.value })}>
                  {["breakfast", "lunch", "snack"].map(m => <option key={m} value={m} className="capitalize">{m}</option>)}
                </select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Temp at Packing (°C)</label><input type="number" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. 85" value={form.temp_at_packing || ""} onChange={e => setForm({ ...form, temp_at_packing: parseFloat(e.target.value) })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Temp at Delivery (°C)</label><input type="number" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. 65" value={form.temp_at_delivery || ""} onChange={e => setForm({ ...form, temp_at_delivery: parseFloat(e.target.value) })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <RatingInput value={form.taste_rating || 3} onChange={v => setForm({ ...form, taste_rating: v })} label="Taste (1-5)" />
              <RatingInput value={form.appearance_rating || 3} onChange={v => setForm({ ...form, appearance_rating: v })} label="Appearance" />
              <RatingInput value={form.hygiene_rating || 3} onChange={v => setForm({ ...form, hygiene_rating: v })} label="Hygiene" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Complaints Count</label><input type="number" min="0" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.complaints_count || 0} onChange={e => setForm({ ...form, complaints_count: parseInt(e.target.value) || 0 })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Inspector Name</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.inspector_name || ""} onChange={e => setForm({ ...form, inspector_name: e.target.value })} /></div>
            </div>
            {(form.complaints_count || 0) > 0 && <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Complaint Details</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.complaint_details || ""} onChange={e => setForm({ ...form, complaint_details: e.target.value })} /></div>}
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes / Corrective Action</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSaveFood} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Inspection</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Material Quality Modal */}
      {modal === "material" && (
        <Modal title="Material Quality Check" onClose={() => setModal(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date *</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.date || today} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Supplier Name</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.supplier_name || ""} onChange={e => setForm({ ...form, supplier_name: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle No.</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.vehicle_number || ""} onChange={e => setForm({ ...form, vehicle_number: e.target.value })} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase text-[#8A7E73]">Items Checked</label>
                <button onClick={addItemCheck} className="text-xs text-[#E8956D] font-bold">+ Add Item</button>
              </div>
              <div className="border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="grid grid-cols-[1fr_80px_80px_80px_100px_70px] bg-[#F4F3F0] px-3 py-2 gap-2">
                  {["Item","Bill Qty","Rcvd Qty","Freshness","Condition","Rejected"].map(h => <span key={h} className="text-[10px] font-bold uppercase text-[#5C5147]">{h}</span>)}
                </div>
                {itemsChecked.map((item, i) => (
                  <div key={`item-${i}`} className="grid grid-cols-[1fr_80px_80px_80px_100px_70px] px-3 py-2 gap-2 border-t border-[#F4F3F0] items-center">
                    <select className="border border-[#E5E1DB] rounded px-2 py-1 text-xs outline-none" value={item.item_name} onChange={e => updateItemCheck(i, "item_name", e.target.value)}>
                      <option value="">Select</option>
                      {masterItems.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                    </select>
                    <input type="number" step="0.1" value={item.bill_qty || ""} onChange={e => updateItemCheck(i, "bill_qty", parseFloat(e.target.value) || 0)} className="border border-[#E5E1DB] rounded px-2 py-1 text-xs w-full outline-none" />
                    <input type="number" step="0.1" value={item.received_qty || ""} onChange={e => updateItemCheck(i, "received_qty", parseFloat(e.target.value) || 0)} className="border border-[#E5E1DB] rounded px-2 py-1 text-xs w-full outline-none" />
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(n => <button key={n} type="button" onClick={() => updateItemCheck(i, "freshness_rating", n)} className={`w-6 h-6 rounded text-[10px] font-bold ${n <= item.freshness_rating ? "bg-[#E8956D]/20 text-[#E8956D]" : "text-[#C4BAAF]"}`}>{n}</button>)}
                    </div>
                    <select className="border border-[#E5E1DB] rounded px-1 py-1 text-xs outline-none" value={item.condition} onChange={e => updateItemCheck(i, "condition", e.target.value)}>
                      {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <input type="number" step="0.1" value={item.rejected_qty || ""} onChange={e => updateItemCheck(i, "rejected_qty", parseFloat(e.target.value) || 0)} className="border border-[#E5E1DB] rounded px-2 py-1 text-xs w-full outline-none" />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Overall Condition</label>
              <div className="flex gap-2">
                {CONDITIONS.map(c => (
                  <button key={c.value} type="button" onClick={() => setForm({ ...form, overall_condition: c.value })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all ${form.overall_condition === c.value ? "border-[#3A322C]" : "border-[#E5E1DB]"} ${c.color}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Inspector Name</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.inspector_name || ""} onChange={e => setForm({ ...form, inspector_name: e.target.value })} /></div>
            </div>
            {/* Photo upload */}
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Photos</label>
              <div className="flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div key={`item-${i}`} className="relative">
                    <img src={p} alt="" className="w-16 h-16 object-cover rounded-lg border border-[#E5E1DB]" />
                    <button onClick={() => setPhotos(prev => prev.filter((_,j) => j !== i))} className="absolute -top-1 -right-1 w-5 h-5 bg-[#C2624E] text-white rounded-full text-xs flex items-center justify-center">✕</button>
                  </div>
                ))}
                <button onClick={() => photoRef.current.click()} className="w-16 h-16 border-2 border-dashed border-[#E5E1DB] rounded-lg flex flex-col items-center justify-center text-[#8A7E73] hover:border-[#E8956D] hover:text-[#E8956D]">
                  <Camera size={16} /><span className="text-[10px] mt-1">Add</span>
                </button>
              </div>
              <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { Array.from(e.target.files).forEach(addPhoto); e.target.value = ''; }} />
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSaveMaterial} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Quality Check</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
